// ===============================================================
// geo.js - client-side (browser/WebView) GeoIP lookup
// path: src/utils/geo.js
// ===============================================================
// detectUserCountry() performs a direct browser/WebView fetch to
// ipapi.co's own-IP endpoint, which resolves THIS caller's own real
// connection IP exactly as it does for any ordinary browser request - no
// server IP-extraction involved at all. This is the ONLY country-
// detection mechanism in this codebase; its result is persisted (when
// successful) through functions/auth/reportClientCountry.js, a separate
// authenticated callable.
//
// *** TRUST BOUNDARY - READ BEFORE USING THIS VALUE FOR ANYTHING ***
// The result of detectUserCountry() is CLIENT-REPORTED, never
// independently server-verified. A tampered client, a modified APK, a
// VPN, or simply a buggy WebView can report any ISO2 code at all - the
// server-side save path (functions/auth/reportClientCountry.js) trusts
// the VALUE but not its ACCURACY, and records it under a distinctly-
// named status ("resolved_client_reported") for exactly this reason.
// This value must never be used for authorization, payment/pricing
// eligibility, legal eligibility, fraud/abuse decisions, authentication,
// or any other security-relevant decision - see that file's header.
//
// ACCOUNT ISOLATION: this module never reads or writes any
// localStorage/sessionStorage key for the detected country - nothing
// here can leak a value detected for one account into a later account
// signed in on the same device/browser. Scheduling state for the bounded
// retry sequence (src/context/AccountSessionStateContext.js) lives only
// in an in-memory React ref, scoped to that provider's own lifetime, and
// is reset on every uid change (see that file).
//
// This file does NOT call any server-side resolver, extract a server-
// side client IP, or depend on GEOIP_COUNTRY_ENABLED/GEOIP_IPINFO_TOKEN -
// none of that exists in this codebase. The only server involvement is
// the separate, authenticated reportClientCountry callable, invoked only
// after this function already has a valid ISO2 code.

const DETECTION_TIMEOUT_MS = 4000;

const ISO2_RE = /^[A-Z]{2}$/;

/**
 * @param {*} raw
 * @return {string|null} the uppercase ISO2 code, or null if invalid.
 */
function normalizeIso2(raw) {
  const v = String(raw || "").trim().toUpperCase();
  return ISO2_RE.test(v) ? v : null;
}

// ipapi.co's official API documentation (https://ipapi.co/api/, verified
// directly during this correction) documents the ISO 3166-1 alpha-2 code
// under TWO field names: `country` (the field actually present in the
// documented /json/ own-IP example response, e.g. `"country": "US"`) and
// `country_code` (listed in the same docs' general field table with the
// identical description, "country code (2 letter, ISO 3166-1 alpha-2)",
// for compatibility with ipapi.co's other endpoint shapes). `country`
// is read first since it is the field the /json/ endpoint's own example
// actually shows; `country_code` is read only as a fallback, for forward
// compatibility if a future response omits `country`. `country_name`
// (the full country name, e.g. "United States") is NEVER read here or
// mistaken for an ISO2 code - it is a different, human-readable field.
function extractIso2FromResponse(data) {
  const fromCountry = normalizeIso2(data && data.country);
  if (fromCountry) return fromCountry;
  return normalizeIso2(data && data.country_code);
}

/**
 * Performs exactly one bounded-timeout fetch to ipapi.co's own-IP
 * endpoint from the browser/WebView - never the explicit-IP form (that
 * would require an IP to put in the URL, which this function never has
 * or needs; the browser/WebView's own network stack supplies the
 * caller's IP implicitly, exactly like any ordinary page load). HTTPS
 * only. `cache: "no-store"` ensures every call is a real request, never
 * a stale cached response. Never throws into onboarding/session/
 * navigation UI - every failure mode (HTTP error, provider error
 * payload in an HTTP-200 body, malformed JSON, invalid/unknown country,
 * timeout, external cancellation, offline, CORS failure, or any other
 * network failure) normalizes to a classified, non-throwing result
 * object instead. Never logs the provider response body, headers, IP,
 * or any personal data - only the fixed status string this function
 * itself derives.
 *
 * EXTERNAL CANCELLATION: an optional `signal` lets the caller (the
 * bounded retry scheduler in AccountSessionStateContext.js) abort the
 * actual in-flight request immediately on logout/uid-change/unmount -
 * not just stop waiting for it. This coexists safely with this
 * function's own internal bounded timeout: both are wired to the SAME
 * underlying fetch via one internal AbortController, so whichever fires
 * first (the timeout, or the caller's external signal) aborts the
 * request exactly once. The two causes are distinguished in the result:
 * an external abort yields "aborted" (never retried, never reported -
 * the session that wanted this result is already gone), while the
 * internal timeout yields "timeout" (a normal, retryable outcome).
 * @param {{timeoutMs?: number, fetchImpl?: Function, signal?: AbortSignal}}
 *   [options] - injectable for tests; production call sites always pass
 *   `signal` (owned by the scheduler) but never need to pass the others.
 * @return {Promise<{status: string, country: (string|null)}>} status is
 *   one of: "ok", "timeout" (this function's own bounded timeout fired),
 *   "aborted" (the caller's external signal fired, i.e. the session
 *   that requested this lookup is gone - never retryable, never
 *   reported), "rate_limited" (HTTP 429), "client_error" (another
 *   non-429 4xx), "server_error" (5xx), "provider_error" (an HTTP-200
 *   body the provider itself marks as an error - ipapi.co's documented
 *   `{error: true}` shape), "invalid_response" (malformed JSON body),
 *   "unknown_country" (no usable country field), "network_error" (the
 *   fetch itself failed, e.g. offline/DNS/CORS - ipapi.co does not send
 *   CORS headers for every failure mode, and a browser reports a CORS-
 *   blocked response as an ordinary network-level fetch rejection,
 *   indistinguishable from "offline" at this layer). Every status other
 *   than "ok", "aborted", and the caller-classified "alreadyLocked"/
 *   "resolved" success cases is treated identically by every caller: a
 *   normal, safely-retryable, non-fatal unresolved result, within the
 *   scheduler's own fixed, bounded attempt budget (see that file).
 */
export async function detectUserCountry({
  timeoutMs = DETECTION_TIMEOUT_MS,
  fetchImpl,
  signal: externalSignal,
} = {}) {
  const doFetch = fetchImpl ||
    (typeof fetch !== "undefined" ? fetch : undefined);
  if (typeof doFetch !== "function") {
    return {status: "network_error", country: null};
  }
  if (externalSignal && externalSignal.aborted) {
    return {status: "aborted", country: null};
  }

  const hasAbortController = typeof AbortController !== "undefined";
  const controller = hasAbortController ? new AbortController() : null;
  const timer = controller ?
    setTimeout(() => controller.abort(), timeoutMs) :
    null;
  let onExternalAbort;
  if (controller && externalSignal) {
    onExternalAbort = () => controller.abort();
    externalSignal.addEventListener("abort", onExternalAbort);
  }
  const cleanup = () => {
    if (timer) clearTimeout(timer);
    if (externalSignal && onExternalAbort) {
      externalSignal.removeEventListener("abort", onExternalAbort);
    }
  };

  let res;
  try {
    res = await doFetch("https://ipapi.co/json/", {
      cache: "no-store",
      ...(controller ? {signal: controller.signal} : {}),
    });
  } catch (err) {
    cleanup();
    if (err && err.name === "AbortError") {
      // Distinguish WHY this aborted: the caller's external signal (the
      // owning session is gone - never retryable, never reported) vs.
      // this function's own internal timeout (a normal, retryable
      // outcome). Checked after the fact since both share one
      // controller/signal by construction.
      if (externalSignal && externalSignal.aborted) {
        return {status: "aborted", country: null};
      }
      return {status: "timeout", country: null};
    }
    // Covers network_error, offline, DNS failure, and a CORS-blocked
    // response alike - never includes the exception (which could embed
    // a URL/other detail) in anything returned.
    return {status: "network_error", country: null};
  }
  cleanup();

  if (!res || !res.ok) {
    const httpStatus = res && res.status;
    if (httpStatus === 429) {
      return {status: "rate_limited", country: null};
    }
    if (typeof httpStatus === "number" && httpStatus >= 400 && httpStatus < 500) {
      return {status: "client_error", country: null};
    }
    return {status: "server_error", country: null};
  }

  let data;
  try {
    data = await res.json();
  } catch {
    return {status: "invalid_response", country: null};
  }

  // ipapi.co's own documented error shape: an HTTP 200 response whose
  // JSON body is `{error: true, reason: "..."}` instead of a non-2xx
  // status (seen for e.g. rate-limited/invalid lookups). Checked before
  // reading `country`/`country_code` - an error body has no usable
  // country field anyway, but this gives a more specific, correctly-
  // classified status than falling through to "unknown_country".
  if (data && data.error === true) {
    return {status: "provider_error", country: null};
  }

  const country = extractIso2FromResponse(data);
  if (!country) {
    return {status: "unknown_country", country: null};
  }
  return {status: "ok", country};
}

export const __testables = {normalizeIso2, extractIso2FromResponse};
